import type {
  BunchaPrivateDraftLaunchContext,
  BunchaWorldApi,
  BunchaWorldLaunchContext,
  PublicCoasterRecord,
} from './contracts';

export const BUNCHA_COASTERS_WORLD_KEY = 'buncha-coasters' as const;

export type WorldLaunchResolution =
  | { kind: 'neutral'; reason: 'sdk_unavailable' | 'context_unavailable' }
  | { kind: 'direct'; context: BunchaWorldLaunchContext }
  | { kind: 'private'; context: BunchaPrivateDraftLaunchContext }
  | { kind: 'blocked'; reason: string };

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export async function resolveWorldLaunch(
  world: BunchaWorldApi | undefined,
): Promise<WorldLaunchResolution> {
  if (!world) return { kind: 'neutral', reason: 'sdk_unavailable' };
  let result;
  try {
    result = await world.getLaunchContext();
  } catch {
    return { kind: 'neutral', reason: 'context_unavailable' };
  }
  if (result.status === 'unavailable') return { kind: 'neutral', reason: 'context_unavailable' };
  if (result.status !== 'available' || !result.context)
    return { kind: 'blocked', reason: 'The signed Buncha Games launch context is invalid.' };
  const context = result.context;
  if ('type' in context && context.type === 'private_draft_share') {
    if (
      !world.getLaunchAssertion ||
      !world.privateDraftLoaded ||
      context.readOnly !== true ||
      context.worldKey !== BUNCHA_COASTERS_WORLD_KEY ||
      context.worldSlug !== BUNCHA_COASTERS_WORLD_KEY
    )
      return { kind: 'blocked', reason: 'This private Coaster launch is invalid.' };
    return { kind: 'private', context: { ...context } };
  }
  if (
    context.worldKey !== BUNCHA_COASTERS_WORLD_KEY ||
    context.worldSlug !== BUNCHA_COASTERS_WORLD_KEY ||
    !nonEmpty(context.publicCreationId) ||
    !nonEmpty(context.immutableRevisionId) ||
    !nonEmpty(context.publicCreationSlug)
  )
    return { kind: 'blocked', reason: 'This launch context does not identify a Buncha Coaster.' };
  return { kind: 'direct', context: { ...context } };
}

export function matchesWorldLaunch(
  record: PublicCoasterRecord,
  context: BunchaWorldLaunchContext,
): boolean {
  return (
    record.publicCoasterId === context.publicCreationId &&
    record.immutableRevisionId === context.immutableRevisionId
  );
}

/**
 * Keeps the signed creation context in game-owned memory while orientation,
 * media-permission, or sign-in prompts temporarily interrupt the journey.
 */
export class WorldLaunchSession {
  private initialization: Promise<WorldLaunchResolution> | null = null;
  private resolution: WorldLaunchResolution | null = null;
  private loadedNotified = false;

  constructor(private readonly world: BunchaWorldApi | undefined) {}

  initialize(): Promise<WorldLaunchResolution> {
    if (!this.initialization)
      this.initialization = resolveWorldLaunch(this.world).then((resolution) => {
        this.resolution = resolution;
        return resolution;
      });
    return this.initialization;
  }

  currentContext(): BunchaWorldLaunchContext | null {
    return this.resolution?.kind === 'direct' ? this.resolution.context : null;
  }

  creationReady(): boolean {
    if (!this.world || this.loadedNotified || this.resolution?.kind !== 'direct') return false;
    this.loadedNotified = true;
    try {
      this.world.creationLoaded();
      return true;
    } catch {
      return false;
    }
  }

  privateDraftReady(): boolean {
    if (
      !this.world?.privateDraftLoaded ||
      this.loadedNotified ||
      this.resolution?.kind !== 'private'
    )
      return false;
    this.loadedNotified = true;
    try {
      this.world.privateDraftLoaded();
      return true;
    } catch {
      return false;
    }
  }

  openContentHub(options?: { creatorPublicId?: string }): boolean {
    if (!this.world) return false;
    try {
      this.world.openContentHub(options);
      return true;
    } catch {
      return false;
    }
  }

  clearDirectContext(): void {
    if (this.resolution?.kind !== 'direct' && this.resolution?.kind !== 'private') return;
    const neutral = { kind: 'neutral', reason: 'context_unavailable' } as const;
    this.resolution = neutral;
    this.initialization = Promise.resolve(neutral);
  }
}
